import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/ui-blocks/PageHeader";
import { api } from "@/lib/api";
import { Link, useParams } from "react-router-dom";
import { SEO } from "@/lib/seo";
import { ShieldCheck, Scale, Laptop, Trees, Users, ChevronRight } from "lucide-react";

const SLUG_META = {
  "know-your-rights": { Icon: ShieldCheck, tag: "Rights" },
  "consumer": { Icon: Users, tag: "Consumer" },
  "legal": { Icon: Scale, tag: "Legal" },
  "digital-safety": { Icon: Laptop, tag: "Digital" },
  "environmental": { Icon: Trees, tag: "Environment" },
};

export function AwarenessHubPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/awareness-modules").then(setItems).catch(() => {}); }, []);
  return (
    <main>
      <SEO title="Awareness Hub" description="Citizen-friendly modules on rights, consumer, legal, digital safety and environment." breadcrumbs={[{name: "Home", href:"/"},{name:"Awareness", href:"/awareness"}]} />
      <PageHeader eyebrow="Awareness Hub" title="Learn. Lead. Serve." lead="Short, verified modules to help every citizen exercise their rights and stay safe." />
      <section className="container-wide py-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((m) => {
          const Meta = SLUG_META[m.category] || SLUG_META[m.slug] || { Icon: ShieldCheck, tag: "Awareness" };
          const Icon = Meta.Icon;
          return (
            <Link key={m.id} to={`/awareness/${m.slug}`} data-testid={`awareness-card-${m.slug}`} className="group rounded-2xl border border-border bg-background overflow-hidden card-lift">
              <div className="aspect-[16/9] overflow-hidden"><img src={m.image} loading="lazy" alt={m.title} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform" /></div>
              <div className="p-5">
                <div className="flex items-center gap-2"><span className="h-9 w-9 rounded-md bg-[hsl(var(--surface-tint))] text-foreground grid place-items-center"><Icon className="h-4.5 w-4.5" /></span><span className="text-xs uppercase tracking-widest text-[hsl(var(--green))]">{Meta.tag}</span></div>
                <h3 className="heading-3 mt-3 text-lg group-hover:text-[hsl(var(--orange))]">{m.title}</h3>
                <p className="text-sm text-[hsl(var(--muted-ink))] mt-2">{m.summary}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-foreground">Explore <ChevronRight className="h-4 w-4" /></div>
              </div>
            </Link>
          );
        })}
      </section>
    </main>
  );
}

export function AwarenessTopicPage() {
  const { slug } = useParams();
  const [module, setModule] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/awareness-modules/${slug}`).then(setModule).catch(() => setErr("Not found")); }, [slug]);
  if (err) return <main className="container-wide py-24"><p>Module not found. <Link to="/awareness" className="underline">Back to Awareness</Link></p></main>;
  if (!module) return <main className="container-wide py-24"><p className="text-[hsl(var(--muted-ink))]">Loading…</p></main>;
  return (
    <main>
      <SEO title={module.title} description={module.summary} breadcrumbs={[{name: "Home", href:"/"},{name:"Awareness", href:"/awareness"},{name: module.title, href:`/awareness/${module.slug}`}]} jsonLd={{ "@context":"https://schema.org", "@type":"Article", headline: module.title, description: module.summary, image: module.image }} />
      <PageHeader eyebrow={"Awareness • " + module.title} title={module.title} lead={module.summary} />
      <section className="container-wide py-10 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="rounded-2xl overflow-hidden border border-border"><img src={module.image} alt={module.title} className="w-full h-64 md:h-80 object-cover" /></div>
          <div className="mt-8 space-y-6">
            {(module.topics || []).map((t, i) => (
              <div key={i} className="rounded-2xl border border-border bg-background p-6" data-testid={`awareness-topic-${i}`}>
                <div className="eyebrow">Topic {i + 1}</div>
                <h3 className="heading-3 mt-2 text-xl">{t.title}</h3>
                <p className="text-[hsl(var(--muted-ink))] mt-2 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-2xl border border-border bg-[hsl(var(--surface-tint))] p-6">
            <div className="eyebrow">More in Awareness</div>
            <div className="mt-3 space-y-2 text-sm">
              {["know-your-rights","consumer","legal","digital-safety","environmental"].filter(s => s !== module.slug).map((s) => (
                <Link key={s} to={`/awareness/${s}`} className="block px-3 py-2 rounded-md hover:bg-background text-foreground">{s.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</Link>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}
